import { AdminFetchGigsafeConfig, AdminSaveGigsafeConfig } from 'src/constants/actionTypes'
import type { GigsafeConfig } from 'src/interfaces/gigsafeConfig'
import type { Action } from 'src/utils/reduxUtils'
import type { GigsafeConfigState } from './types'

function resolveApiErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const apiError = error as { message?: string; payload?: { message?: string } }

    if (typeof apiError.payload?.message === 'string') {
      return apiError.payload.message
    }

    if (typeof apiError.message === 'string') {
      return apiError.message
    }
  }

  return fallback
}

const getInitialState = (): GigsafeConfigState => ({
  partnerId: null,
  config: null,
  loading: false,
  loaded: false,
  error: null,
  saving: false,
})

export const gigsafeConfigReducer = (
  state: GigsafeConfigState = getInitialState(),
  action: Action<unknown>,
): GigsafeConfigState => {
  switch (action.type) {
    case AdminFetchGigsafeConfig.type.REQUEST: {
      const partnerId = (action.payload as { partnerId?: string } | undefined)?.partnerId
      const isSamePartner = Boolean(partnerId) && state.partnerId === partnerId

      return {
        ...state,
        partnerId: partnerId ?? state.partnerId,
        config: isSamePartner ? state.config : null,
        loaded: isSamePartner ? state.loaded : false,
        loading: true,
        error: null,
      }
    }
    case AdminFetchGigsafeConfig.type.SUCCESS: {
      const payload = action.payload as
        | { partnerId: string; config: GigsafeConfig | null }
        | undefined

      if (payload === undefined || payload.partnerId !== state.partnerId) {
        return state
      }

      return {
        ...state,
        config: payload.config,
        loading: false,
        loaded: true,
        error: null,
      }
    }
    case AdminFetchGigsafeConfig.type.FAILED:
      return {
        ...state,
        loading: false,
        error: resolveApiErrorMessage(action.payload, 'Failed to load customer settings'),
      }

    case AdminSaveGigsafeConfig.type.REQUEST:
      return {
        ...state,
        saving: true,
      }
    case AdminSaveGigsafeConfig.type.SUCCESS: {
      const payload = action.payload as { partnerId: string; config: GigsafeConfig } | undefined

      if (payload === undefined || payload.partnerId !== state.partnerId) {
        return state
      }

      return {
        ...state,
        config: payload.config,
        loaded: true,
        saving: false,
      }
    }
    case AdminSaveGigsafeConfig.type.FAILED:
      return {
        ...state,
        saving: false,
      }

    default:
      return state
  }
}
